"use client";

import {DashboardFilter} from "@/components/@filter/dashboard-filter";
import {Modal, ModalBody, ModalContent, ModalFooter} from "@nextui-org/react";
import React, {useEffect, useState} from "react";
import {AiFillCloseCircle} from "react-icons/ai";
import {DeActivateModal, DeleteModal, ResetPasswordModal} from ".";
import {NeoButton} from "../forms";
import {
  BookingMapModalContent,
  CancelBookingModalContent
} from "../pages/bookings/manageBookings/card";
import {ManageBookingDetailView} from "../pages/bookings/manageBookings/manageBooking.shared";
import {
  SosCommentConfirmModal,
  SOSDetailView
} from "../pages/bookings/sos/sosDetailView";
import {TransactionDetailView} from "../pages/bookings/transactions/transaction-detail-view";
import {AttachmentTripRating} from "../pages/bookings/tripRating/attachmentTripRating";
import {TripRatingDetailView} from "../pages/bookings/tripRating/tripRating.shared";
import {ContactForm} from "../pages/contact/contact-form";
import {CreditsPointForm} from "../pages/creditspointsoverview/creditspointform";
import {CreditsPointsOverview} from "../pages/creditspointsoverview/creditspointsoverview";
import {DashboardReport} from "../pages/dashboard/dashboard-report";
import {DashboardSetting} from "../pages/dashboard/dashboard-setting";
import {WidgetOrdering} from "../pages/dashboard/widgetordering";
import {
  AllRequestsModal,
  PreviewModal,
  ReviewModal
} from "../pages/dashboard/widgets/Widget-MissingFeatures";
import {DriverFormv2} from "../pages/drivers/driver-formv2";
import {DocumentsGrid} from "../pages/expensereports/documentgrid";
import {ExpenseAddCommonForm} from "../pages/expensereports/expenseadd";
import {ExpenseFuelForm} from "../pages/expensereports/expensefuelform";
import {ExpenseMaintanceForm} from "../pages/expensereports/expensemaintenanceform";
import {ExpenseMiscellaneousForm} from "../pages/expensereports/expensemiscellaneousform";
import {FleetForm} from "../pages/fleet/fleetform";
import {FuelAnalytics} from "../pages/fuelanalytics/fuelanalytics";
import {FuelConsumption} from "../pages/fuelanalytics/fuelconsumption";
import {PaymentReminder} from "../pages/live/paymentReminder";
import {ProfileForm} from "../pages/profile/profile-form";
import {CommonReportTable} from "../pages/reports/commonTableView";
import {ReportUi} from "../pages/reports/report-ui";
import {ReportTableCardRenderer} from "../pages/reports/reportTableCardRenderer";
import {CreateRoutePlan} from "../pages/routes/planOptimizer/createPlanForm";
import {ProcessingPopup} from "../pages/routes/planOptimizer/processingPopup";
import {DeletetBpwindow} from "../pages/routes/routePlanWidgets/optimizer/deleteBp/deleteForm";
import {EditBpwindow} from "../pages/routes/routePlanWidgets/optimizer/editBp/bpEditForm";
import {InputParamsError} from "../pages/routes/routePlanWidgets/optimizer/leftWidgets/view_download_outputData/inputError";
import {ViewOutputData} from "../pages/routes/routePlanWidgets/optimizer/leftWidgets/view_download_outputData/viewOutputData";
import {UserMappingUnMapping} from "../pages/routes/routePlanWidgets/optimizer/modals/userMappingUnMapping";
import {BpHistory} from "../pages/users/bp-History";
import {ChangePickupDropModal} from "../pages/users/change-pickup-drop-modal";
import {EditUser} from "../pages/users/edit-user";
import {EditVehicleMappingDetailsModal} from "../pages/users/edit-vehicle-mapping-details-modal";
import {RfidEdit} from "../pages/users/rfid-edit";
import {SmartEdit} from "../pages/users/smart-edit";
import {AssignDriver} from "../pages/vehicles/assigndriver";
import {EditVehicleConfirmation} from "./editVehicleConfirmation";
import {ConfirmationPopup} from "./routePlan/confirmation";
import {SuccessfulModal} from "./successful.modal";

type NeoModalProps = {
  data?: any | null;
  actionType: string | null;
  onOpen: any;
  onClose: any;
  onModalSubmit?: (payload?: any) => void;
  actionText?: string;
  hasActions?: boolean;
  selectedRow?: any;
  isDismissable?: boolean;
  customContent?: React.ReactNode;
  modalSize?: ModalSize;
  showCloseIcon?: boolean;
};

type ModalSize =
  | "4xl"
  | "md"
  | "xs"
  | "sm"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "5xl"
  | "full"
  | undefined;

/**
 * Generic modal dialog for all the popups
 *
 * If you need the buttons to be controlled from the component
 * please pass hasActions as true, and create a function called onModalSubmit
 * and pass them as functional reference
 * If you dont want yes & no button from neo modal design the button inside the component and pass hasActions to false.
 * You dont have to pass the functional refernce for modal submit you only need onclose & onopen
 * For example, please check neo-table where both the usecase are handled
 *
 */
export const NeoModal: React.FC<NeoModalProps> = ({
  actionType,
  onOpen,
  onClose,
  onModalSubmit,
  actionText = "Yes",
  data,
  hasActions = true,
  selectedRow,
  isDismissable,
  customContent,
  modalSize,
  showCloseIcon
}) => {
  const [size, setSize] = useState<ModalSize>("md");
  const [content, setContent] = useState<React.ReactNode | null>(null);
  const [CloseIcon, setCloseIcon] = useState(false);
  const [hideCloseButton, setHideCloseButton] = useState(false);
  const [backdrop, setBackdrop] = useState<"blur" | "opaque" | "transparent">(
    "blur"
  );
  useEffect(() => {
    if (!onOpen) {
      setContent(null);
      return;
    }
    setBackdrop("blur");
    switch (actionType) {
      case "custom":
        setContent(customContent ?? null);
        setSize(modalSize ?? "5xl");
        setCloseIcon(showCloseIcon ?? true);
        break;
      case "delete":
        setSize("md");
        setContent(<DeleteModal />);
        break;
      case "smart_edit":
        setSize("2xl");
        setContent(<SmartEdit selectedRow={selectedRow} onClose={onClose} />);
        break;
      case "change-mobile":
        setContent(
          <ProfileForm
            mobileNumber={data?.mobileNumber}
            formType={actionType}
            onClose={onClose}
          />
        );
        break;
      case "change-password":
        setContent(
          <ProfileForm
            mobileNumber={data?.mobileNumber}
            formType={actionType}
            onClose={onClose}
          />
        );
        break;
      case "reset_rfid":
        setSize("4xl");
        setContent(<RfidEdit onClose={onClose} selectedRow={selectedRow} />);
        break;
      case "resetpassword":
        setSize("md");
        setContent(<ResetPasswordModal />);
        break;
      case "deactivate":
        setSize("lg");
        setContent(<DeActivateModal />);
        break;
      case "report":
        setSize("full");
        setContent(<ReportUi reportdata={data} />);
        setCloseIcon(true);
        break;
      case "contactform":
        setSize("lg");
        setContent(<ContactForm />);
        setCloseIcon(true);
        break;
      case "dashboardreport":
        setSize(
          data?.activeReport?.customView === "busAttendanceOverview" ||
            data?.activeReport?.customView === "campusAttendanceOverview"
            ? "full"
            : "5xl"
        );
        setContent(<DashboardReport reportdata={data} />);
        setCloseIcon(true);
        break;
      case "dashboardsetting":
        setSize("md");
        setContent(
          <DashboardSetting
            key={`dashboardsetting-${data?.showFuelLimit ? "fuel" : "base"}`}
            onSubmit={onModalSubmit}
            settingdatas={data}
          />
        );
        setCloseIcon(true);
        break;
      case "dashboardfilter":
        setSize("md");
        setContent(
          <DashboardFilter onSubmit={onModalSubmit} searchinstitutes={data} />
        );
        setCloseIcon(true);
        break;
      case "widgetordering":
        setSize("lg");
        setContent(
          <WidgetOrdering onSubmit={onModalSubmit} orderingdatas={data} />
        );
        setCloseIcon(true);
        break;
      case "missingFeaturePreview":
        setSize("5xl");
        setContent(data ? <PreviewModal {...data} /> : null);
        setCloseIcon(true);
        break;
      case "missingFeatureReview":
        setSize("xl");
        setContent(<ReviewModal {...data} />);
        setCloseIcon(true);
        break;
      case "missingFeatureAllRequests":
        setSize("xl");
        setContent(<AllRequestsModal {...data} />);
        setCloseIcon(true);
        break;
      case "paymentReminder":
        setSize("lg");
        setContent(<PaymentReminder data={data} />);
        setCloseIcon(true);
        break;
      case "addeditDriver":
        setSize("xl");
        setContent(<DriverFormv2 data={data} onClose={onClose} />);
        setCloseIcon(true);
        break;
      case "assigndriver":
        setSize("md");
        setContent(
          <AssignDriver datas={data} onSubmit={onModalSubmit ?? (() => {})} />
        );
        break;
      case "fleetform":
        setSize("md");
        setContent(
          <FleetForm
            header={actionText}
            datas={data}
            onSubmit={onModalSubmit ?? (() => {})}
          />
        );
        setCloseIcon(true);
        break;
      case "editVehicleConfirmation":
        setSize("md");
        setContent(<EditVehicleConfirmation data={data} />);
        break;
      case "fuelform":
        setSize("4xl");
        setContent(
          <ExpenseFuelForm
            key={data?.id ?? "new"}
            data={{...data}}
            onSubmit={onModalSubmit ?? (() => {})}
            onClose={onClose ?? (() => {})}
          />
        );
        setCloseIcon(true);
        break;
      case "maintenanceform":
        setSize("4xl");
        setContent(
          <ExpenseMaintanceForm
            key={data?.id ?? "new"}
            data={{...data}}
            onSubmit={onModalSubmit ?? (() => {})}
            onClose={onClose ?? (() => {})}
          />
        );
        setCloseIcon(true);
        break;
      case "miscellaneousform":
        setSize("4xl");
        setContent(
          <ExpenseMiscellaneousForm
            key={data?.id ?? "new"}
            data={{...data}}
            onSubmit={onModalSubmit ?? (() => {})}
            onClose={onClose ?? (() => {})}
          />
        );
        setCloseIcon(true);
        break;
      case "documentgrid": {
        const docsCount: any = data?.documents?.length;
        if (docsCount === 1) {
          setSize("md");
        } else if (docsCount == 2) {
          setSize("2xl");
        } else if (docsCount == 3) {
          setSize("4xl");
        }

        setContent(
          <DocumentsGrid
            key={data?.id ?? "new"}
            title="Documents"
            data={data}
            onSubmit={onModalSubmit ?? (() => {})}
          />
        );
        setCloseIcon(true);
        break;
      }
      case "expensesadd":
        setSize("xs");
        setContent(
          <ExpenseAddCommonForm onSubmit={onModalSubmit ?? (() => {})} />
        );
        setCloseIcon(true);
        break;
      case "addPlanOptimizer":
        setSize("sm");
        setContent(<CreateRoutePlan editData={data} onClose={onClose} />);
        //setCloseIcon(true);
        break;
      case "creditUsageOverview":
        setSize("full");
        setContent(<CreditsPointsOverview />);
        setCloseIcon(true);
        break;
      case "creditspointform":
        setSize("md");
        setContent(
          <CreditsPointForm
            onSubmit={onModalSubmit ?? (() => {})}
            onClose={onClose ?? (() => {})}
          />
        );
        setCloseIcon(true);
        break;
      case "tableView":
        setSize("5xl");
        setContent(<CommonReportTable reportdata={data} />);
        setCloseIcon(true);
        break;
      case "tablecard":
        setSize("full");
        setContent(<ReportTableCardRenderer reportdata={data} />);
        setCloseIcon(true);
        break;
      case "routePlanProcessingPopup":
        setSize("sm");
        setContent(
          <ProcessingPopup
            changeVariant={data.changeVariant}
            newAddedVariantId={data.newAddedVariantId}
            setIsHistoryTab={data.setIsHistoryTab}
            type={data.type ? data.type : "newVariant"}
            closePopup={onClose}
          />
        );
        setHideCloseButton(true);
        break;
      case "userMappping":
        setSize("sm");
        setContent(
          <UserMappingUnMapping
            routeData={data.routeData}
            userData={data.userData}
            variantId={data.variantId}
            setCurrentVariant={data.setCurrentVariant}
            refreshSingleRoute={data.refreshSingleRoute}
            handleCloseModal={data.handleCloseModal}
            index={data.index}
          />
        );
        break;
      case "boardingConformation":
        setSize("sm");
        setContent(
          <ConfirmationPopup
            onConfirm={data.onConfirm}
            message={data.message}
            closePopup={onClose}
            reqData={data.reqData}
          />
        );
        break;
      case "exchangeRoute":
        setSize("sm");
        setContent(
          <EditBpwindow
            variantId={data.variantId}
            sourceRoute={data.sourceRoute}
            bpData={data.bpData}
            variantRoute={data.variantRoute}
            currentVariant={data.currentVariant}
            setCurrentVariant={data.setCurrentVariant}
            closePopup={onClose}
            refreshSingleRoute={data.refreshSingleRoute}
          />
        );
        break;
      case "deleteRouteBP":
        setSize("sm");
        setContent(
          <DeletetBpwindow
            variantId={data.variantId}
            sourceRoute={data.sourceRoute}
            bpData={data.bpData}
            variantRoute={data.variantRoute}
            currentVariant={data.currentVariant}
            setCurrentVariant={data.setCurrentVariant}
            closePopup={onClose}
            refreshSingleRoute={data.refreshSingleRoute}
          />
        );
        break;
      case "fuelanalytics":
        setSize("full");
        setCloseIcon(true);
        setContent(<FuelAnalytics reportdata={data} />);
        break;
      case "fuelconsumption":
        setSize("full");
        setCloseIcon(true);
        setContent(<FuelConsumption reportdata={data} />);
        break;

      case "viewDownloadRPOutput":
        setSize("5xl");
        setContent(
          <>
            <ViewOutputData
              exportData={data.exportData}
              selectedOption={data.selectedOption}
              vehicleTableData={data.vehicleTableData}
            />
          </>
        );
        setCloseIcon(true);
        break;
      case "routeOptimizerInputError":
        setSize("md");
        setContent(<InputParamsError errorMessage={data.errorMessage} />);
        break;
      case "imagePreview":
        setSize("md");
        setBackdrop("opaque");
        setContent(
          <div className="mt-7 flex items-center justify-center p-2">
            <img
              src={data?.imageSrc}
              alt="Preview"
              className="max-h-[70vh] max-w-full rounded-xl object-contain"
              onError={e => {
                (e.target as HTMLImageElement).src = "/images/avatar.png";
              }}
            />
          </div>
        );
        setCloseIcon(true);
        break;
      case "attachmentPreviewTripRating":
        setSize("md");
        setContent(
          <AttachmentTripRating
            imageSrc={data?.imageSrc}
            fileType={data?.fileType}
          />
        );
        setCloseIcon(true);
        break;
      case "tripRatingDetailView":
        setSize("full");
        setContent(
          data?.trip ? (
            <TripRatingDetailView trip={data.trip} onClose={onClose} />
          ) : null
        );
        setCloseIcon(true);
        break;
      case "transactionDetailView":
        setSize("full");
        setContent(
          data?.trip ? <TransactionDetailView trip={data.trip} /> : null
        );
        setCloseIcon(true);
        break;
      case "manageBookingDetailView":
        setSize("full");
        setContent(
          <ManageBookingDetailView
            booking={data?.booking}
            statusType={data?.statusType ?? 1}
            onClose={onClose}
          />
        );
        setCloseIcon(true);
        break;
      case "sosDetailView":
        setSize("full");
        setContent(
          <SOSDetailView
            card={data?.card}
            currentTab={data?.currentTab}
            onStatusChange={data?.onStatusChange}
            onClose={onClose}
          />
        );
        setCloseIcon(true);
        break;
      case "sosCommentConfirm":
        setSize("sm");
        setContent(
          <SosCommentConfirmModal
            userName={data?.userName}
            bookingId={data?.bookingId}
            label={data?.label}
            onConfirm={data?.onConfirm}
            onClose={onClose}
          />
        );
        setCloseIcon(true);
        break;
      case "bookingMap":
        setSize("full");
        setContent(
          <BookingMapModalContent
            booking={data?.booking}
            routeName={data?.routeName}
            mode={data?.mode}
            uiDetail={data?.uiDetail}
            statusType={data?.statusType}
          />
        );
        setCloseIcon(true);
        break;
      case "cancelBooking":
        setSize("sm");
        setContent(
          <CancelBookingModalContent
            vehicleName={data?.vehicleName}
            onConfirm={data?.onConfirm}
            onClose={onClose}
          />
        );
        setCloseIcon(true);
        break;
      case "cancelTracker":
        setSize("sm");
        setContent(
          <div className="p-4 text-center text-sm text-gray-600">
            {data?.message}
          </div>
        );
        setCloseIcon(true);
        break;

      case "editUserDetails":
        setSize("5xl");
        setContent(<EditUser data={data} />);
        setCloseIcon(true);
        break;

      case "editVehicleMappingDetails":
        setSize("5xl");
        setContent(<EditVehicleMappingDetailsModal data={data} />);
        setCloseIcon(true);
        break;

      case "changePickupDrop":
        setSize("full");
        setContent(<ChangePickupDropModal data={data} />);
        setCloseIcon(true);
        break;
      case "bpHistory":
        setSize("full");
        setContent(
          <BpHistory
            key={selectedRow?.id}
            selectedRow={selectedRow}
            onClose={onClose}
          />
        );
        setCloseIcon(true);
        break;

      case "SuccessfullModal":
        setSize("md");
        setContent(
          <SuccessfulModal success={data?.success} message={data?.message} />
        );
        setCloseIcon(false);
        break;

      default:
        setContent(null);
    }
  }, [
    actionType,
    onOpen,
    data?.mobileNumber,
    data?.message,
    data?.success,
    customContent,
    modalSize,
    onClose,
    onModalSubmit,
    selectedRow,
    showCloseIcon
  ]);

  return (
    <Modal
      backdrop="blur"
      scrollBehavior={actionType === "report" ? "outside" : undefined}
      size={size}
      classNames={{closeButton: "z-50"}}
      isOpen={onOpen}
      onClose={onClose}
      closeButton={CloseIcon ? <AiFillCloseCircle size={50} /> : ""}
      hideCloseButton={hideCloseButton}
      // classNames={{closeButton: "z-30"}}
      isKeyboardDismissDisabled
      isDismissable={false}
    >
      <ModalContent>
        {onClose => (
          <>
            <ModalBody
              className="p-4"
              style={
                CloseIcon
                  ? {
                      flex: 1,
                      overflowY: "auto",
                      maxHeight: "100vh"
                    }
                  : {}
              }
            >
              {content}
            </ModalBody>
            {hasActions && (
              <ModalFooter className="justify-center py-5">
                <NeoButton color="white" onPress={onClose}>
                  Close
                </NeoButton>
                <NeoButton
                  color={actionType === "delete" ? "red" : "black"}
                  // onPress={onModalSubmit(() => selectedRow ? selectedRow : {})}
                  onPress={() => onModalSubmit?.(selectedRow ?? {})}
                >
                  {actionText}
                </NeoButton>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
